import { ApiRequestError, Button } from '@h2hspark/ui';
import { CreditCard, Lock, ShieldCheck } from 'lucide-react';
import * as React from 'react';

import {
  CARD_ENTRY_ASSURANCE,
  CHECKOUT_SECTION_CARD,
  CARD_FIELD_LABELS,
  CARD_TOKEN_EXCHANGE_FAILED,
  NAME_ON_CARD_PLACEHOLDER,
  PROVIDER_DRAWN_FIELDS,
  NEW_CARD_HEADING,
  NO_CARD_ELEMENTS_AVAILABLE,
  PAYING_LABEL,
  PAYMENT_SECTION_HEADING,
  SAVED_CARDS_HEADING,
  WEB_CARD_FIELDS,
  payActionLabel,
  savedCardExpiryLine,
  savedCardLine,
} from './constants';
import type { CardElementsHost, CardFieldsValidity, SavedCard } from './types';

/**
 * **The Payment method section: the cards this member has paid with, the provider's own fields,
 * and the one control that takes money** (`MEM-021` AC-1, AC-2, AC-3).
 *
 * ## Transcribed from `Member App.dc.html`, the payment sheet
 *
 * "Payment method" over a list of card rows — a brand mark, "Visa ending 4242" and
 * "Expires 08/28 · saved" — then the fields for a new card, then the pay action carrying the
 * total. The prototype draws the card fields as ordinary inputs; they are not, and cannot be, and
 * the deviation is named in `checkout-page.tsx` and in the pull request.
 *
 * ## Nothing in this component ever holds a card detail
 *
 * `MEM-021` AC-3 is that no card number, expiry, security code or PAN reaches an H2HSpark server,
 * log, database, backup, error trace or monitoring payload. The way a React form breaks that is
 * not a decision — it is an `onChange` that keeps a value "to validate it", and a `console.log`
 * or an error tracker that then serialises the component's state.
 *
 * So the fields are **not this component's**. Each is an empty `<div>` the provider mounts an
 * iframe into (`constants.ts` and `types.ts`), on the provider's own origin, which this application's script
 * cannot read across. The only state here is: whether the provider says its fields are complete,
 * the provider's own message when they are not, and whether a request is in flight. There is no
 * `value`, no `onChange` on a card field, and no way to add one without adding a method to
 * `CardElementsHost` — which is a diff a reviewer sees, and which
 * `no-card-detail-leaves-the-browser.test.ts` fails the build over.
 *
 * ## The saved cards are a list, not a wallet
 *
 * `PAY-001` AC-4 and ADR 045 deliberately refuse the platform any way to list, create or delete a
 * stored payment method, and `MEM-021` does not widen them. What {@link PaymentMethodPanel} renders is
 * the four display values off the payments this member **has already made** — the provider
 * reported them attached to a charge, and the order row kept them.
 *
 * So there is no delete control and no "add a card" that saves one for later: a card appears here
 * because a payment succeeded, and paying with it goes back through the provider's fields above.
 * A member checking out for the first time sees no list at all, which is the data rather than an
 * empty state.
 *
 * ## Why there is no radio group
 *
 * A control that let a member *choose* a saved card would be a promise this platform cannot keep:
 * it holds no token for any of them, so "pay with this one" would still have to go through the
 * provider's fields. Offering the choice and then asking for the card again is worse than not
 * offering it. The list says what the member has paid with; the fields are how they pay.
 */
export function PaymentMethodPanel({
  total,
  savedCards,
  elements,
  onPay,
  pending,
  failure,
}: {
  /** The total line's server-rendered amount — `$30.00`. Never formatted here. */
  readonly total: string;
  /** `MEM-021` AC-1. Empty for a member who has never paid, which is the ordinary first case. */
  readonly savedCards: ReadonlyArray<SavedCard>;
  /** The provider's elements, or `null` when no provider is configured (ADR 045). */
  readonly elements: CardElementsHost | null;
  /** Handed the provider's token and nothing else. */
  readonly onPay: (paymentMethodToken: string) => void;
  readonly pending: boolean;
  readonly failure: Error | null;
}): React.ReactElement {
  const [validity, setValidity] = React.useState<CardFieldsValidity>({
    complete: false,
    message: null,
  });
  const [tokenFailure, setTokenFailure] = React.useState<string | null>(null);
  /**
   * **The one value a member types that this component holds** (`MEM-021` AC-2, ruled 2026-08-30).
   *
   * The provider has no hosted element for a cardholder name, so this field is ours. It is state,
   * with an `onChange`, which is exactly the shape the header below warns about for the other
   * three — and the distinction is not that a name looks harmless. It is that a name is **not
   * chargeable** and **never leaves this browser**: it goes into
   * `elements.createPaymentMethodToken` and from there into the provider's own script. It is not
   * put in `onPay`, which carries the token alone, so no request this application makes can
   * contain it.
   */
  const [nameOnCard, setNameOnCard] = React.useState('');

  return (
    <section className={`${CHECKOUT_SECTION_CARD} space-y-3.5`} data-testid="payment-section">
      <h2 className="font-brand text-sm font-bold text-foreground">{PAYMENT_SECTION_HEADING}</h2>

      {savedCards.length === 0 ? null : (
        <div className="space-y-1.5">
          <h3 className="text-xs font-medium uppercase tracking-caps text-text-subtle">
            {SAVED_CARDS_HEADING}
          </h3>
          <ul className="space-y-1.5" data-testid="saved-cards">
            {savedCards.map((card) => (
              <li
                key={`${card.brand}-${card.last4}-${String(card.expiryMonth)}-${String(card.expiryYear)}`}
                className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3 text-sm"
              >
                {/* The brand mark AC-1 asks for. A glyph rather than a network logo: shipping
                    seven trademarked marks is a licensing question this story has no answer to,
                    and the brand is named in words beside it either way. */}
                <CreditCard className="h-4 w-4 shrink-0 text-text-subtle" aria-hidden />
                <span className="text-text-body">{savedCardLine(card.brand, card.last4)}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {savedCardExpiryLine(card.expiryMonth, card.expiryYear)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {elements === null ? (
        // No provider configured (ADR 045). A stated absence, never a disabled Pay button: a
        // greyed-out control reads as a transient failure somebody comes back and retries.
        <p
          role="status"
          className="rounded-lg border border-dashed border-border bg-muted/50 p-3.5 text-sm text-muted-foreground"
        >
          {NO_CARD_ELEMENTS_AVAILABLE}
        </p>
      ) : (
        <div className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-caps text-text-subtle">
            {NEW_CARD_HEADING}
          </h3>
          <CardFields
            elements={elements}
            onValidityChange={setValidity}
            nameOnCard={nameOnCard}
            onNameOnCardChange={setNameOnCard}
          />
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{CARD_ENTRY_ASSURANCE}</span>
          </p>

          <Button
            /* §3.1's pay control: the spark gradient at `--radius-pill` under `--shadow-accent`,
               with the lock the prototype puts in front of the label. The same treatment the
               event page's reserve bar already carries, which is the point — the control that
               takes the money and the control that promised it look like one another. The label
               itself is `payActionLabel` and unchanged; it carries the total. */
            className="w-full rounded-pill bg-spark text-white shadow-accent hover:bg-spark sm:py-[15px] sm:text-md"
            size="lg"
            data-testid="pay-action"
            disabled={pending || !validity.complete || nameOnCard.trim() === ''}
            onClick={() => {
              setTokenFailure(null);
              // The exchange happens in the provider's own code: it reads its own iframes, takes
              // the one value this component holds, and answers with a reference. That reference
              // is the only thing it hands back, and the only thing handed upwards.
              void elements
                .createPaymentMethodToken({ nameOnCard: nameOnCard.trim() })
                .then((paymentMethodToken) => {
                  onPay(paymentMethodToken);
                })
                .catch((error: unknown) => {
                  // The provider's own message about its own fields. Composing one here would be
                  // guessing at a field this application cannot see.
                  setTokenFailure(
                    error instanceof Error && error.message.length > 0
                      ? error.message
                      : CARD_TOKEN_EXCHANGE_FAILED,
                  );
                });
            }}
          >
            <Lock className="mr-2 h-[18px] w-[18px]" aria-hidden />
            {pending ? PAYING_LABEL : payActionLabel(total)}
          </Button>
        </div>
      )}

      {validity.message === null ? null : (
        <p role="status" className="text-sm text-muted-foreground">
          {validity.message}
        </p>
      )}
      {tokenFailure === null ? null : (
        <p role="status" className="text-sm text-destructive">
          {tokenFailure}
        </p>
      )}
      {failure === null ? null : <PaymentFailure error={failure} />}
    </section>
  );
}

/**
 * **The four regions of the card form: three the provider draws into, one that is ours**
 * (`MEM-021` AC-2, AC-3).
 *
 * The regions, their order and their labels are the prototype's and are unchanged. What differs
 * between them is who owns the input inside.
 *
 * **Number, expiry and security code are the provider's.** Each is an empty node it mounts an
 * iframe into, on its own origin, which this application's script cannot read across. That is what
 * makes AC-3 structural rather than a matter of discipline.
 *
 * **The name on the card is ours, and that is a ruling rather than a shortcut.** Stripe Elements
 * has no hosted element for a cardholder name; its own guidance is to collect it in the site's own
 * input and pass it as billing details. Sumit ruled on 2026-08-30 that this is what to build,
 * rather than amend AC-2 down to three fields or adopt the Address Element, which would collect a
 * full postal address nobody asked for. It is safe because a name is not chargeable - it is in
 * neither `PAY-001` AC-4's list nor `MEM-021` AC-3's - and because the value goes into the
 * provider's script and reaches no H2HSpark server, log or error trace.
 *
 * `useEffect` rather than `useLayoutEffect` because mounting is the provider's asynchronous work
 * and nothing here measures the result - and because `apps/web`'s jsdom harness has to be imported
 * before any component or Radix disables `useLayoutEffect` process-wide, which is a trap worth not
 * walking towards.
 */
function CardFields({
  elements,
  onValidityChange,
  nameOnCard,
  onNameOnCardChange,
}: {
  readonly elements: CardElementsHost;
  readonly onValidityChange: (validity: CardFieldsValidity) => void;
  readonly nameOnCard: string;
  readonly onNameOnCardChange: (value: string) => void;
}): React.ReactElement {
  const nodes = React.useRef(new Map<string, HTMLDivElement>());

  React.useEffect(() => {
    // Only the provider's own three. Asking it to mount the name region would be asking for a
    // field it does not have.
    const teardowns = PROVIDER_DRAWN_FIELDS.flatMap((field) => {
      const node = nodes.current.get(field);
      return node === undefined ? [] : [elements.mount(field, node)];
    });
    teardowns.push(elements.onValidityChange(onValidityChange));
    return () => {
      for (const teardown of teardowns) teardown();
    };
  }, [elements, onValidityChange]);

  return (
    /* §3.1's field stack: the label in the brand face at `--text-sm`, 7px above a 52px control on
       `--surface-card` with a 1.5px hairline at `--radius-md`. What was here was a 12px label over
       a 40px box, which is the density of a settings form rather than of the screen somebody types
       a card number into. */
    <div className="space-y-3.5">
      {WEB_CARD_FIELDS.map((field) => (
        <div key={field} className="space-y-1.5">
          <span
            className="block font-brand text-sm font-semibold text-foreground"
            id={`card-field-${field}`}
          >
            {CARD_FIELD_LABELS[field]}
          </span>
          {field === 'name' ? (
            <input
              // The one card-form input this application owns. `autoComplete="cc-name"` is the
              // browser's own hint for it. There is no `name` attribute, because this input is
              // never inside a form that submits anywhere - the value's only exit is the
              // provider's own script.
              type="text"
              autoComplete="cc-name"
              spellCheck={false}
              aria-labelledby={`card-field-${field}`}
              data-testid={`card-field-${field}`}
              className="h-[52px] w-full rounded-md border-[1.5px] border-border bg-card px-3.5 text-md text-text-body placeholder:text-text-subtle"
              placeholder={NAME_ON_CARD_PLACEHOLDER}
              value={nameOnCard}
              onChange={(event) => {
                onNameOnCardChange(event.target.value);
              }}
            />
          ) : (
            <div
              // The provider's iframe lands here. There is no `value`, no `onChange` and no `ref`
              // this application reads from - see `types.ts` on why that is the whole of
              // `MEM-021` AC-3 rather than a detail of it.
              aria-labelledby={`card-field-${field}`}
              data-testid={`card-field-${field}`}
              /* `justify-center` and the muted type are for the **stand-in**, which writes a
                 sentence into this node; the provider's real element is an iframe that sizes and
                 styles itself from `STRIPE_ELEMENT_APPEARANCE` and inherits none of it. Harmless
                 to the one, and the difference between a legible test mode and a clipped one. */
              className="flex h-[52px] flex-col justify-center overflow-hidden rounded-md border-[1.5px] border-border bg-card px-3.5 text-md text-text-subtle"
              ref={(node) => {
                if (node === null) nodes.current.delete(field);
                else nodes.current.set(field, node);
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The server's own sentence, shown as written.
 *
 * The same component `RegistrationPanel` and the checkout screen both use, and the same argument:
 * what a member reads is what the server actually applied. It matters most here — the sentence for
 * a seat that went while the payment was going through says the member has been refunded, and a
 * message this screen composed could not know that.
 */
function PaymentFailure({ error }: { readonly error: Error }): React.ReactElement {
  const message =
    error instanceof ApiRequestError && error.message.length > 0
      ? error.message
      : 'Something went wrong on our side. Nothing was charged. Try again in a moment.';
  return (
    <p role="status" className="text-sm text-destructive">
      {message}
    </p>
  );
}
